/**
 * Simple in-memory cache with TTL (time-to-live) support
 * Implements stale-while-revalidate pattern for better UX
 */

class ApiCache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
  }

  /**
   * Generate cache key from URL and params
   */
  generateKey(url, params = {}) {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${JSON.stringify(params[key])}`)
      .join('&');
    return `${url}${paramString ? '?' + paramString : ''}`;
  }

  /**
   * Get cached data
   * Returns { data, isStale } where isStale indicates if data needs refresh
   */
  get(key) {
    const cached = this.cache.get(key);

    if (!cached) {
      return { data: null, isStale: false };
    }

    const now = Date.now();
    const age = now - cached.timestamp;

    // Data is fresh (within TTL)
    if (age < cached.ttl) {
      return { data: cached.data, isStale: false };
    }

    // Data is stale but still usable (stale-while-revalidate)
    // Return stale data but indicate it needs refresh
    if (age < cached.ttl * 2) {
      return { data: cached.data, isStale: true };
    }

    // Data is too old, remove it
    this.cache.delete(key);
    return { data: null, isStale: false };
  }

  /**
   * Set cache data with TTL
   */
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Invalidate specific cache key
   */
  invalidate(key) {
    this.cache.delete(key);
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern) {
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    });
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Get cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const apiCache = new ApiCache();

/**
 * Higher-order function to wrap API calls with caching
 *
 * @param {Function} apiFn - The API function to wrap
 * @param {Object} options - Caching options
 * @param {number} options.ttl - Time to live in milliseconds
 * @param {string} options.key - Custom cache key (optional)
 * @returns {Function} Wrapped API function with caching
 *
 * @example
 * const getCachedUser = withCache(getUser, { ttl: 5 * 60 * 1000 });
 * const userData = await getCachedUser();
 */
export function withCache(apiFn, options = {}) {
  const { ttl, key: customKey } = options;

  return async function(...args) {
    // Generate cache key
    const cacheKey = customKey || apiCache.generateKey(
      apiFn.name,
      args.length > 0 ? { args: args } : {}
    );

    // Check cache first
    const { data: cachedData, isStale } = apiCache.get(cacheKey);

    if (cachedData && !isStale) {
      console.log(`[Cache HIT] ${cacheKey}`);
      return cachedData;
    }

    // If data is stale, return it immediately but refresh in background
    if (cachedData && isStale) {
      console.log(`[Cache STALE] ${cacheKey} - returning stale data and refreshing`);

      // Refresh in background (don't await)
      apiFn(...args)
        .then(freshData => {
          apiCache.set(cacheKey, freshData, ttl);
          console.log(`[Cache REFRESHED] ${cacheKey}`);
        })
        .catch(error => {
          console.error(`[Cache REFRESH FAILED] ${cacheKey}:`, error);
        });

      return cachedData;
    }

    // Cache miss - fetch fresh data
    console.log(`[Cache MISS] ${cacheKey}`);
    try {
      const freshData = await apiFn(...args);
      apiCache.set(cacheKey, freshData, ttl);
      return freshData;
    } catch (error) {
      // On error, still return stale data if available
      if (cachedData) {
        console.warn(`[Cache ERROR] ${cacheKey} - returning stale data due to error:`, error);
        return cachedData;
      }
      throw error;
    }
  };
}

export default apiCache;
